import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getApiService, ServiceType } from "./apiServices";
import { SENDER_ENDPOINTS } from './apiEndPoints';
import { senderKeys } from './senderApi';


// Types

interface SearchtravellersRequest {
  fromLocation: number;
  toLocation?: number;
  startDate: string;
  endDate: string;
  sortBy: string;
  page: number;
  limit: number;
}
interface CreateItemsRequest {
  name: string;
  user_id: string;
  departure_airport_id: string;
  destination_airport_id: string;
  shipping_start_date: string;
  shipping_end_date: string;
  total_weight: number;
  total_price: number;
  receiver: {
    name: string;
    email: string;
    contact_number: string;
  };
  items: Array<{
    category_id: string;
    pieces: number;
    pages?: number;
    weight: number;
    price: number;
    image_ids: number[];
  }>;
}

// Sender sends offer to traveler (initiates offer)
export interface SendOfferRequest {
  shipment_id: string;
  itinerary_id: string;
  success_url: string;
  cancel_url: string;
  expiration_duration?: number;
}

// Sender accepts traveler's offer request
export interface AcceptOfferRequestBySender {
  order_code: string;
  success_url: string;
  cancel_url: string;
}

// Sender rejects traveler's offer request
export interface RejectOfferRequestBySender {
  order_code: string;
  reason?: string;
}

// Sender withdraws their offer request
export interface WithdrawOfferRequestBySender {
  order_code: string;
}

// Drop-off request for sender
export interface DropOffRequest {
  order_id: string;
  user_id: string;
  check_in_by: string;
  id_card_front_id: string;
  id_card_back_id: string;
  item_images: string[];
}

// Receiver checkout request
export interface ReceiverCheckoutRequest {
  order_code: string;
  check_out_by: string;
  signature: string;
  comment?: string;
}

// Update receiver information request
export interface UpdateReceiverRequest {
  order_id: string;
  receiver_name?: string;
  receiver_email?: string;
  receiver_phone_no?: string;
}

// Update shipment item (edit cart)
export interface UpdateShipmentItemRequest {
  order_id: string;
  id: string;
  pieces?: number | 0;
  weight?: number | 0.0;
  price?: number | 0;
  pages?: number | 0.00;
  image_ids?: number[];
  success_url?: string;
  cancel_url?: string;
}

// Create claim request
export interface CreateClaimRequest {
  order_id: string;
  user_id: string;
  reason: string;
  description: string;
  item_images: string[];
  role?: string;
}

// Claim reason interface
export interface ClaimReason {
  title: string;
  value: string;
  description?: string;
}

// Claim details interface
export interface ClaimDetails {
  claim_id: string;
  order_id: string;
  user_id: string;
  reason: string;
  description: string;
  item_images: string[];
  status: string;
  created_at: string;
  updated_at: string;
}

// Sender preferences interface
export interface SenderPreferences {
  user_id: string;
  currency_id: string;
  unit_metrics_for_distance: string;
  distance_radius: number;
  add_shipment_protection: boolean;
}

// Update sender preferences request
export interface UpdateSenderPreferencesRequest {
  user_id: string;
  currency_id?: string;
  unit_metrics_for_distance?: string;
  distance_radius?: number;
  add_shipment_protection?: boolean;
}

// Currency interface
export interface Currency {
  id: string;
  code: string;
  name: string;
  symbol: string;
}

// Payment method interfaces
export interface SetupIntentRequest {
  sender_id: string;
}

// Create setup intent for payment element
export interface CreateSetupIntentRequest {
  user_id: string;
}

// Verify and save card request
export interface VerifyAndSaveCardRequest {
  user_id: string;
  setupIntentId: string;
}

export interface AttachPaymentMethodRequest {
  sender_id: string;
  paymentMethodId: string;
  set_as_default?: boolean;
}

export interface SetPaymentMethodDefaultRequest {
  sender_id: string;
  payment_method_id: string;
}

export interface DeletePaymentMethodRequest {
  sender_id: string;
  payment_method_id: string;
}

// Create new shipment request
export interface CreateNewShipmentRequest {
  user_id: string;
  name: string;
  departure_airport_id: string;
  destination_airport_id: string;
  shipping_start_date: string;
  shipping_end_date: string;
  total_weight: number;
  total_price: number;
  description?: string;
  receiver: {
    name: string;
    email: string;
    contact_number: string;
  };
  items: Array<{
    category_id: string;
    image_ids: string[];
  }>;
}

// Update existing shipment request
export interface UpdateExistingShipmentRequest {
  id: string;
  user_id: string;
  name: string;
  departure_airport_id: string;
  destination_airport_id: string;
  shipping_start_date: string;
  shipping_end_date: string;
  total_weight: number;
  total_price: number;
  description?: string;
  receiver: {
    name: string;
    email: string;
    contact_number: string;
  };
  items: Array<{
    category_id: string;
    image_ids: string[];
  }>;
  delete_clothing_Ids: string[];
  delete_document_Ids: string[];
  delete_electronics_Ids: string[];
  delete_other_Ids: string[];
}

// API functions
const senderApiClient = {
  // Base query function
  async request({ url, method = 'GET', body }: { url: string; method?: string; body?: unknown }) {
    try {
      const senderService = getApiService(ServiceType.SENDER2);
      const response = await senderService({
        url,
        method,
        data: body,
      });
      return response.data;
    } catch (error: unknown) {
      const err = error as {
        response?: {
          status?: number;
          data?: unknown;
        };
        message?: string;
      };
      throw {
        status: err.response?.status,
        data: err.response?.data || err.message,
      };
    }
  },


  createItems: (params: CreateItemsRequest) =>
    senderApiClient.request({
      url: SENDER_ENDPOINTS.CREATE_ITEMS.path,
      method: 'POST',
      body: params,
    }),

  getShipmentListing: (user_id: string, page = 1, limit = 10, sort = 'created_at_desc') =>
    senderApiClient.request({
      url: `${SENDER_ENDPOINTS.SHIPMENT_LISTING.path}?user_id=${user_id}&page=${page}&limit=${limit}&sort=${sort}`,
      method: 'GET',
    }),

  getShipmentCategories: async (countryName?: string) => {
    const params = new URLSearchParams();
    if (countryName) params.append('countryName', countryName);
    const queryParams = params.toString() ? `?${params.toString()}` : '';
    const response = await senderApiClient.request({
      url: SENDER_ENDPOINTS.CATEGORY_LISTING.path + queryParams,
    });
    return response;
  },

  getShipmentDetails: (id: string) =>
    senderApiClient.request({
      url: SENDER_ENDPOINTS.SHIPMENT_DETAILS.path.replace(':id', id),
      method: 'GET',
    }),

  getOrdersListing: (sender_id: string, filter?: string, page = 1, limit = 10) => {
    const params = new URLSearchParams();
    if (filter !== undefined) params.append('filter', filter);
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    const queryParams = params.toString() ? `?${params.toString()}` : '';
    return senderApiClient.request({
      url: SENDER_ENDPOINTS.ORDERS_LISTING.path.replace(':sender_id', sender_id) + queryParams,
      method: 'GET',
    });
  },
  acceptOfferAsSender: (payload: unknown) =>
    senderApiClient.request({
      url: SENDER_ENDPOINTS.ACCEPT_OFFER.path,
      method: 'POST',
      body: payload,
    }),

  getOrderDetails: (order_id: string) =>
    senderApiClient.request({
      url: SENDER_ENDPOINTS.ORDER_DETAILS.path.replace(':order_id', order_id),
      method: 'GET',
    }),

  // Sender sends offer to traveler
  sendOffer: (params: SendOfferRequest) =>
    senderApiClient.request({
      url: SENDER_ENDPOINTS.SEND_OFFER_AS_SENDER.path,
      method: 'POST',
      body: params,
    }),
  trackOrderAsSender: (order_id: string) =>
    senderApiClient.request({
      url: SENDER_ENDPOINTS.TRACK_ORDER_AS_SENDER.path.replace(':order_id', String(order_id)),
    }),

  checkItems: (userId: string) =>
    senderApiClient.request({
      url: SENDER_ENDPOINTS.SEARCH_ITEMS_CHECK.path.replace(':userId', String(userId)),
    }),

  // Sender accepts traveler's offer request
  acceptOfferRequest: (params: AcceptOfferRequestBySender) =>
    senderApiClient.request({
      url: SENDER_ENDPOINTS.ACCEPT_OFFER_REQUEST.path,
      method: 'POST',
      body: params,
    }),

  // Sender rejects traveler's offer request
  rejectOfferRequest: (params: RejectOfferRequestBySender) =>
    senderApiClient.request({
      url: SENDER_ENDPOINTS.REJECT_OFFER_REQUEST.path,
      method: 'POST',
      body: params,
    }),

  // Sender withdraws their offer request
  withdrawOfferRequest: (params: WithdrawOfferRequestBySender) =>
    senderApiClient.request({
      url: SENDER_ENDPOINTS.WITHDRAW_OFFER_REQUEST.path,
      method: 'POST',
      body: params,
    }),

  // Update shipment item (edit cart)
  updateShipmentItem: ({ order_id, id, ...body }: UpdateShipmentItemRequest) =>
    senderApiClient.request({
      url: SENDER_ENDPOINTS.EDIT_CART.path
        .replace(':order_id', order_id)
        .replace(':id', id),
      method: 'PUT',
      body,
    }),


  searchtravellers: (payload: SearchtravellersRequest & {
    traveller_id: number;
    fromLocation: number;
    toLocation?: number;
    startDate: string;
    endDate: string;
    page: number;
    limit: number;
    radius: number;
    latitude: number;
    longitude: number;
    sortBy?: string;
    min_weight?: number;
    max_weight?: number;
  }) =>
    senderApiClient.request({
      url: SENDER_ENDPOINTS.TRAVEL_TRIPS.path,
      method: 'POST',
      body: payload,
    }),

  // Drop-off for sender
  dropOff: (params: DropOffRequest) =>
    senderApiClient.request({
      url: SENDER_ENDPOINTS.DROP_OFF.path,
      method: 'POST',
      body: params,
    }),

  // Receiver checkout
  receiverCheckout: (params: ReceiverCheckoutRequest) =>
    senderApiClient.request({
      url: SENDER_ENDPOINTS.RECEIVER_CHECKOUT.path,
      method: 'POST',
      body: params,
    }),

  // Update receiver information
  updateReceiver: ({ order_id, ...body }: UpdateReceiverRequest) =>
    senderApiClient.request({
      url: SENDER_ENDPOINTS.RECEIVER_UPDATE.path.replace(':order_id', order_id),
      method: 'PATCH',
      body,
    }),
  updateItems: ({ shipmentId, payload }: { shipmentId: string; payload: unknown }) =>
    senderApiClient.request({
      url: SENDER_ENDPOINTS.UPDATE_MY_ITEM.path.replace(':shipmentId', String(shipmentId)),
      method: 'PUT',
      body: payload,
    }),
  deleteItem: (itemId: string) =>
    senderApiClient.request({
      url: SENDER_ENDPOINTS.DELETE_MY_ITEM.path.replace(':id', String(itemId)),
      method: 'DELETE',
    }),

  // Create claim
  createClaim: (params: CreateClaimRequest) =>
    senderApiClient.request({
      url: SENDER_ENDPOINTS.CREATE_CLAIM.path,
      method: 'POST',
      body: params,
    }),

  // Get claim reasons
  getClaimReasons: (type?: string) =>
    senderApiClient.request({
      url: type ? `${SENDER_ENDPOINTS.CLAIMS_REASONS.path}?type=${type}` : SENDER_ENDPOINTS.CLAIMS_REASONS.path,
      method: 'GET',
    }),

  // Get claim details
  getClaimDetails: (claim_id: string) =>
    senderApiClient.request({
      url: SENDER_ENDPOINTS.CLAIMS_DETAILS.path.replace(':claim_id', claim_id),
      method: 'GET',
    }),

  // Get payment history for sender
  getPaymentHistory: (sender_id: string) =>
    senderApiClient.request({
      url: SENDER_ENDPOINTS.PAYMENT_HISTORY.path.replace(':sender_id', sender_id),
      method: 'GET',
    }),

  // Download payment receipt (PDF) for a specific order
  getPaymentReceipt: (order_code: string) =>
    senderApiClient.request({
      url: SENDER_ENDPOINTS.PAYMENT_RECEIPT.path.replace(':ordercode', order_code),
      method: 'GET',
    }),

  // Pin itinerary to wishlist
  pinItinerary: (params: { user_id: string; itinerary_id: string }) =>
    senderApiClient.request({
      url: SENDER_ENDPOINTS.PIN_ITINERARY.path,
      method: 'POST',
      body: params,
    }),

  // Unpin itinerary from wishlist
  unpinItinerary: (params: { user_id: string; itinerary_id: string }) =>
    senderApiClient.request({
      url: SENDER_ENDPOINTS.UNPIN_ITINERARY.path,
      method: 'POST',
      body: params,
    }),

  // Get sender preferences
  getSenderPreferences: (sender_id: string) =>
    senderApiClient.request({
      url: SENDER_ENDPOINTS.SENDER_PREFERENCES.path.replace(':sender_id', sender_id),
      method: 'GET',
    }),

  // Update sender preferences
  updateSenderPreferences: (params: UpdateSenderPreferencesRequest) =>
    senderApiClient.request({
      url: SENDER_ENDPOINTS.UPDATE_SENDER_PREFERENCES.path,
      method: 'POST',
      body: params,
    }),

  // Get list of available currencies
  getCurrencies: () =>
    senderApiClient.request({
      url: SENDER_ENDPOINTS.CURRENCY.path,
      method: 'GET',
    }),

  // Get all payment methods for sender
  getPaymentMethods: (sender_id: string) =>
    senderApiClient.request({
      url: SENDER_ENDPOINTS.PAYMENT_METHODS.path.replace(':sender_id', sender_id),
      method: 'GET',
    }),

  // Create setup intent for card addition
  createSetupIntent: (sender_id: string) =>
    senderApiClient.request({
      url: SENDER_ENDPOINTS.SETUP_INTETENT.path.replace(':sender_id', sender_id),
      method: 'POST',
    }),

  // Attach payment method to customer
  attachPaymentMethod: (params: AttachPaymentMethodRequest) =>
    senderApiClient.request({
      url: SENDER_ENDPOINTS.ATTACH_PAYMENT_METHOD.path,
      method: 'POST',
      body: params,
    }),

  // Set payment method as default
  setPaymentMethodDefault: ({ payment_method_id, sender_id }: SetPaymentMethodDefaultRequest) =>
    senderApiClient.request({
      url: SENDER_ENDPOINTS.SET_PAYMENT_METHOD_DEFAULT.path.replace(':payment_method_id', payment_method_id),
      method: 'PATCH',
      body: { sender_id },
    }),

  // Delete payment method
  deletePaymentMethod: ({ payment_method_id, sender_id }: DeletePaymentMethodRequest) =>
    senderApiClient.request({
      url: SENDER_ENDPOINTS.DELETE_METHOD.path.replace(':payment_method_id', payment_method_id),
      method: 'DELETE',
      body: { sender_id },
    }),

  // Create new shipment
  createNewShipment: (params: CreateNewShipmentRequest) =>
    senderApiClient.request({
      url: SENDER_ENDPOINTS.CREATE_NEW_ITEM.path,
      method: 'POST',
      body: params,
    }),

  // Update existing shipment
  updateExistingShipment: ({ id, ...body }: UpdateExistingShipmentRequest) =>
    senderApiClient.request({
      url: SENDER_ENDPOINTS.UPDATE_EXISTING_ITEM.path.replace(':id', id),
      method: 'PUT',
      body,
    }),

  // Search items by name (search uncompleted or cancelled items for sender)
  searchItems: (sender_id: string, name?: string) => {
    const params = new URLSearchParams();
    if (name) params.append('name', name);
    const queryParams = params.toString() ? `?${params.toString()}` : '';
    return senderApiClient.request({
      url: SENDER_ENDPOINTS.CHECK_ITEMS.path.replace(':id', sender_id) + queryParams,
      method: 'GET',
    });
  },

  // Create setup intent for payment element
  createPaymentSetupIntent: (params: CreateSetupIntentRequest) =>
    senderApiClient.request({
      url: SENDER_ENDPOINTS.CREATE_SETUP_INTENT.path,
      method: 'POST',
      body: params,
    }),

  // Verify and save card after user confirms in Payment Element
  verifyAndSaveCard: (params: VerifyAndSaveCardRequest) =>
    senderApiClient.request({
      url: SENDER_ENDPOINTS.VERIFY_AND_SAVE_CARD.path,
      method: 'POST',
      body: params,
    }),

  // Get pending order requests for a specific shipment
  getPendingRequestsByShipment: (sender_id: string, shipment_id: string) =>
    senderApiClient.request({
      url: SENDER_ENDPOINTS.GET_PENDING_REQUESTS_BY_SHIPMENT.path
        .replace(':sender_id', sender_id)
        .replace(':shipment_id', shipment_id),
      method: 'GET',
    }),

  // Get my pending order requests for a specific shipment (status = 'pending')
  getMyPendingRequestsByShipment: (sender_id: string, shipment_id: string) =>
    senderApiClient.request({
      url: SENDER_ENDPOINTS.GET_MY_PENDING_REQUESTS_BY_SHIPMENT.path
        .replace(':sender_id', sender_id)
        .replace(':shipment_id', shipment_id),
      method: 'GET',
    }),

};

// TanStack Query hooks

export const useGetShipmentListingQuery = (
  user_id: string,
  page = 1,
  limit = 10,
  sort = 'created_at_desc',
  options = {}
) =>
  useQuery({
    queryKey: [...senderKeys.myItems(Number(user_id)), page, limit, sort],
    queryFn: () => senderApiClient.getShipmentListing(user_id, page, limit, sort),
    ...options,
  });
export const useGetShipmentCategoriesQuery = (countryName?: string, options = {}) =>
  useQuery({
    queryKey: ['sender', 'shipmentCategories', countryName] as const,
    queryFn: () => senderApiClient.getShipmentCategories(countryName),
    staleTime: 1000 * 60 * 60, // Cache for 1 hour
    ...options,
  });

export const useGetShipmentDetailsQuery = (id: string, options = {}) =>
  useQuery({
    queryKey: ['sender', 'shipmentDetails', id] as const,
    queryFn: () => senderApiClient.getShipmentDetails(id),
    enabled: !!id, // Only run query if id is provided
    ...options,
  });

// Sender accepts traveler's offer request
export const useAcceptOfferRequestMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: AcceptOfferRequestBySender) => senderApiClient.acceptOfferRequest(params),
    onSuccess: () => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: senderKeys.allMyItems });
    },
  });
};

// Sender rejects traveler's offer request
export const useRejectOfferRequestMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: RejectOfferRequestBySender) => senderApiClient.rejectOfferRequest(params),
    onSuccess: () => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: senderKeys.allMyItems });
    },
  });
};

// Sender withdraws their offer request
export const useWithdrawOfferRequestMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: WithdrawOfferRequestBySender) => senderApiClient.withdrawOfferRequest(params),
    onSuccess: () => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: senderKeys.allMyItems });
      queryClient.invalidateQueries({ queryKey: senderKeys.allMyOrders });
    },
  });
};

// Get orders listing for sender
export const useGetOrdersListingQuery = (
  sender_id: string,
  filter?: 'all' | 'in_progress' | 'completed' | 'cancelled' | 'under_review',
  page = 1,
  limit = 10,
  options = {}
) =>
  useQuery({
    queryKey: [...senderKeys.myOrders(Number(sender_id)), filter, page, limit] as const,
    queryFn: () => senderApiClient.getOrdersListing(sender_id, filter, page, limit),
    enabled: !!sender_id,
    ...options,
  });

// Get order details
export const useGetOrderDetailsQuery = (order_id: string, options = {}) =>
  useQuery({
    queryKey: ['sender', 'orderDetails', order_id] as const,
    queryFn: () => senderApiClient.getOrderDetails(order_id),
    enabled: !!order_id,
    ...options,
  });

// Update shipment item (edit cart)
export const useUpdateShipmentItemMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: UpdateShipmentItemRequest) => senderApiClient.updateShipmentItem(params),
    onSuccess: (_data: unknown, variables: UpdateShipmentItemRequest) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: senderKeys.allMyItems });
      queryClient.invalidateQueries({ queryKey: ['sender', 'shipmentDetails', variables.order_id] });
      queryClient.invalidateQueries({ queryKey: ['sender', 'orderDetails', variables.order_id] });
    },
  });
};
export const useSearchtravellersMutation = () => {
  return useMutation({
    mutationFn: ({ payload }: {
      payload: SearchtravellersRequest & {
        traveller_id: number;
        fromLocation: number;
        toLocation?: number;
        startDate: string;
        endDate: string;
        page: number;
        limit: number;
        radius: number;
        latitude: number;
        longitude: number;
        sortBy?: string;
        min_weight?: number;
        max_weight?: number;
      }
    }) => senderApiClient.searchtravellers(payload),
  });
};

// Drop-off mutation for sender
export const useDropOffMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: DropOffRequest) => senderApiClient.dropOff(params),
    onSuccess: () => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: senderKeys.allMyOrders });
      queryClient.invalidateQueries({ queryKey: senderKeys.allMyItems });
      // Invalidate order details so the page reflects updated state after drop-off
      queryClient.invalidateQueries({ queryKey: ['sender', 'orderDetails'] });
    },
  });
};
export const useTrackOrderAsSenderQuery = (order_id: string, options = {}) =>
  useQuery({
    queryKey: ['sender', 'trackOrder', order_id] as const,
    queryFn: () => senderApiClient.trackOrderAsSender(order_id),
    ...options,
  });

// Receiver checkout mutation
export const useReceiverCheckoutMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: ReceiverCheckoutRequest) => senderApiClient.receiverCheckout(params),
    onSuccess: () => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: senderKeys.allMyOrders });
    },
  });
};

// Update receiver information mutation
export const useUpdateReceiverMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: UpdateReceiverRequest & { order_code?: string }) => senderApiClient.updateReceiver(params),
    onMutate: async (variables: UpdateReceiverRequest & { order_code?: string }) => {
      // Use order_code for query key if provided, otherwise fall back to order_id
      const queryKey = variables.order_code || variables.order_id;

      // Cancel outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: ['sender', 'orderDetails', queryKey] });

      // Snapshot previous value
      const previousOrderData = queryClient.getQueryData(['sender', 'orderDetails', queryKey]);

      // Optimistically update the cache
      queryClient.setQueryData(['sender', 'orderDetails', queryKey], (old: unknown) => {
        if (!old) return old;
        const oldData = old as Record<string, unknown>;
        return {
          ...oldData,
          receiver_name: variables.receiver_name ?? oldData.receiver_name,
          receiver_email: variables.receiver_email ?? oldData.receiver_email,
          receiver_phone_number: variables.receiver_phone_no ?? oldData.receiver_phone_number,
        };
      });

      // Return context with snapshot
      return { previousOrderData, queryKey };
    },
    onError: (_error: unknown, _variables: UpdateReceiverRequest & { order_code?: string }, context: { previousOrderData?: unknown; queryKey?: string | number } | undefined) => {
      // Rollback to previous value on error
      if (context?.previousOrderData && context?.queryKey) {
        queryClient.setQueryData(['sender', 'orderDetails', context.queryKey], context.previousOrderData);
      }
    },
    onSettled: (_data: unknown, _error: unknown, variables: UpdateReceiverRequest & { order_code?: string }) => {
      // Use order_code for query key if provided, otherwise fall back to order_id
      const queryKey = variables.order_code || variables.order_id;

      // Refetch to ensure data consistency
      queryClient.invalidateQueries({ queryKey: ['sender', 'orderDetails', queryKey] });
      queryClient.invalidateQueries({ queryKey: senderKeys.allMyOrders });
    },
  });
};
export const useDeleteItemMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (itemId: string) => senderApiClient.deleteItem(itemId),
    onMutate: async (itemId: string) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: senderKeys.allMyItems });
      await queryClient.cancelQueries({ queryKey: ['sender', 'shipmentDetails', itemId] });

      // Snapshot previous values
      const previousMyItems = queryClient.getQueryData(senderKeys.allMyItems);
      const previousShipmentDetails = queryClient.getQueryData(['sender', 'shipmentDetails', itemId]);

      // Optimistically remove item from list
      queryClient.setQueryData(senderKeys.allMyItems, (old: unknown) => {
        const oldData = old as { data?: unknown[] } | undefined;
        if (!oldData?.data) return old;
        return {
          ...oldData,
          data: oldData.data.filter((item: unknown) => (item as Record<string, unknown>).id !== itemId),
        };
      });

      // Remove shipment details from cache
      queryClient.removeQueries({ queryKey: ['sender', 'shipmentDetails', itemId] });

      return { previousMyItems, previousShipmentDetails, itemId };
    },
    onError: (_error: unknown, _itemId: string, context: { previousMyItems?: unknown; previousShipmentDetails?: unknown; itemId?: string } | undefined) => {
      // Rollback on error
      if (context?.previousMyItems) {
        queryClient.setQueryData(senderKeys.allMyItems, context.previousMyItems);
      }
      if (context?.previousShipmentDetails && context?.itemId) {
        queryClient.setQueryData(['sender', 'shipmentDetails', context.itemId], context.previousShipmentDetails);
      }
    },
    onSettled: () => {
      // Refetch to ensure data consistency
      queryClient.invalidateQueries({ queryKey: senderKeys.allMyItems });
    },
  });
};
// Create claim mutation
export const useCreateClaimMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: CreateClaimRequest) => senderApiClient.createClaim(params),
    onSuccess: () => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: senderKeys.allMyOrders });
      queryClient.invalidateQueries({ queryKey: ['sender', 'claims'] });
    },
  });
};

// Get claim reasons query
export const useGetClaimReasonsQuery = (type?: string, options = {}) =>
  useQuery({
    queryKey: ['sender', 'claimReasons', type] as const,
    queryFn: () => senderApiClient.getClaimReasons(type),
    staleTime: 1000 * 60 * 60, // Cache for 1 hour
    ...options,
  });

// Get claim details query
export const useGetClaimDetailsQuery = (claim_id: string, options = {}) =>
  useQuery({
    queryKey: ['sender', 'claimDetails', claim_id] as const,
    queryFn: () => senderApiClient.getClaimDetails(claim_id),
    enabled: !!claim_id,
    ...options,
  });

// Get payment history query
export const useGetPaymentHistoryQuery = (sender_id: string, options = {}) =>
  useQuery({
    queryKey: ['sender', 'paymentHistory', sender_id] as const,
    queryFn: () => senderApiClient.getPaymentHistory(sender_id),
    enabled: !!sender_id,
    ...options,
  });

// Get payment receipt query (PDF download)
export const useGetPaymentReceiptQuery = (order_code: string, options = {}) =>
  useQuery({
    queryKey: ['sender', 'paymentReceipt', order_code] as const,
    queryFn: () => senderApiClient.getPaymentReceipt(order_code),
    enabled: !!order_code,
    ...options,
  });

// Pin itinerary to wishlist mutation
export const usePinItineraryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: { user_id: string; itinerary_id: string }) =>
      senderApiClient.pinItinerary(params),
    onSuccess: () => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['sender', 'favorites'] });
    },
  });
};

// Unpin itinerary from wishlist mutation
export const useUnpinItineraryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: { user_id: string; itinerary_id: string }) =>
      senderApiClient.unpinItinerary(params),
    onSuccess: () => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['sender', 'favorites'] });
    },
  });
};

// Get currencies query
export const useGetCurrenciesQuery = (options = {}) =>
  useQuery({
    queryKey: ['sender', 'currencies'] as const,
    queryFn: () => senderApiClient.getCurrencies(),
    staleTime: 1000 * 60 * 60, // Cache for 1 hour
    ...options,
  });

// Get payment methods query
export const useGetPaymentMethodsQuery = (sender_id: string, options = {}) =>
  useQuery({
    queryKey: ['sender', 'paymentMethods', sender_id] as const,
    queryFn: () => senderApiClient.getPaymentMethods(sender_id),
    enabled: !!sender_id,
    ...options,
  });

// Create setup intent mutation
export const useCreateSetupIntentMutation = () => {
  return useMutation({
    mutationFn: (sender_id: string) => senderApiClient.createSetupIntent(sender_id),
  });
};

// Attach payment method mutation
export const useAttachPaymentMethodMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: AttachPaymentMethodRequest) => senderApiClient.attachPaymentMethod(params),
    onSuccess: (_data: unknown, variables: AttachPaymentMethodRequest) => {
      // Invalidate payment methods query to refresh the list
      queryClient.invalidateQueries({
        queryKey: ['sender', 'paymentMethods', variables.sender_id]
      });
    },
  });
};

// Set payment method as default mutation
export const useSetPaymentMethodDefaultMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: SetPaymentMethodDefaultRequest) => senderApiClient.setPaymentMethodDefault(params),
    onSuccess: (_data: unknown, variables: SetPaymentMethodDefaultRequest) => {
      // Invalidate payment methods query to refresh the list
      queryClient.invalidateQueries({
        queryKey: ['sender', 'paymentMethods', variables.sender_id]
      });
    },
  });
};

// Delete payment method mutation
export const useDeletePaymentMethodMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: DeletePaymentMethodRequest) => senderApiClient.deletePaymentMethod(params),
    onSuccess: (_data: unknown, variables: DeletePaymentMethodRequest) => {
      // Invalidate payment methods query to refresh the list
      queryClient.invalidateQueries({
        queryKey: ['sender', 'paymentMethods', variables.sender_id]
      });
    },
  });
};

// Search items by name query
export const useSearchItemsQuery = (sender_id: string, name?: string, options = {}) =>
  useQuery({
    queryKey: ['sender', 'searchItems', sender_id, name] as const,
    queryFn: () => senderApiClient.searchItems(sender_id, name),
    enabled: !!sender_id,
    ...options,
  });

// Create setup intent for payment element mutation
export const useCreatePaymentSetupIntentMutation = () => {
  return useMutation({
    mutationFn: (params: CreateSetupIntentRequest) => senderApiClient.createPaymentSetupIntent(params),
  });
};

// Verify and save card mutation
export const useVerifyAndSaveCardMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: VerifyAndSaveCardRequest) => senderApiClient.verifyAndSaveCard(params),
    onSuccess: (_data: unknown, variables: VerifyAndSaveCardRequest) => {
      // Invalidate payment methods query to refresh the list after card is saved
      queryClient.invalidateQueries({
        queryKey: ['sender', 'paymentMethods', variables.user_id]
      });
    },
  });
};

// Get pending order requests by shipment ID query
export const useGetPendingRequestsByShipmentQuery = (
  sender_id: string,
  shipment_id: string,
  options = {}
) =>
  useQuery({
    queryKey: ['sender', 'pendingRequests', sender_id, shipment_id] as const,
    queryFn: () => senderApiClient.getPendingRequestsByShipment(sender_id, shipment_id),
    enabled: !!sender_id && !!shipment_id,
    ...options,
  });

// Get my pending order requests by shipment ID query (status = 'pending')
export const useGetMyPendingRequestsByShipmentQuery = (
  sender_id: string,
  shipment_id: string,
  options = {}
) =>
  useQuery({
    queryKey: ['sender', 'myPendingRequests', sender_id, shipment_id] as const,
    queryFn: () => senderApiClient.getMyPendingRequestsByShipment(sender_id, shipment_id),
    enabled: !!sender_id && !!shipment_id,
    ...options,
  });